import { useChartPeriod } from '@/hooks';

import {
  CurrencyValue,
  LineChart,
  formatCurrency,
  formatDatetime,
  formatNumber
} from '@blockanalitica/ui';
import { useCallback, useMemo } from 'react';

export default function HistoricGraphs({
  data,
  timeFilter,
  activeDisplayOption,
  chartDaysAgo,
  displayOptions
}) {
  const [, updateChartConfigWithPeriod] = useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod({
    options: {
      datasets: {
        line: {
          fill: false
        }
      },
      scales: {
        y: {
          stacked: false,
          beginAtZero: true,
          ticks: {
            callback: function (value) {
              return Number(value) >= 0 ? formatCurrency(Number(value)) : '';
            }
          }
        }
      }
    }
  });

  const yScaleValueFormatter = useCallback(
    (value) => `${formatCurrency(value)}`,
    []
  );

  const valueFormatter = useCallback(
    (value) => (
      <CurrencyValue
        value={Number(value)}
        options={{
          currency: 'USD',
          iconSize: '3'
        }}
      />
    ),
    []
  );

  const datasetStyles = useMemo(
    () => ({
      aggregate_backstop_capital: {},
      sky_backstop_capital: {
        borderColor: 'rgba(228, 188, 56)',
        backgroundColor: 'rgba(228, 188, 56, 0.8)'
      },
      spark_backstop_capital: {
        borderColor: 'rgba(250, 67, 189, 1)',
        backgroundColor: 'rgba(250, 67, 189, 0.8)'
      }
    }),
    []
  );

  const datasetLabel = displayOptions?.find(
    (option) => option.key === activeDisplayOption
  )?.value;

  const normalizedData = useMemo(() => {
    if (activeDisplayOption !== 'sky_backstop_capital') {
      return data;
    }

    return data.map((item) => {
      const value = Number(item[activeDisplayOption]);
      return {
        ...item,
        [activeDisplayOption]: value > 0 ? 0 : Math.abs(value)
      };
    });
  }, [data, activeDisplayOption]);
  return (
    <LineChart
      key={activeDisplayOption}
      data={normalizedData}
      datasets={[
        {
          xField: 'date',
          yField: activeDisplayOption,
          label: datasetLabel,
          ...datasetStyles[activeDisplayOption]
        }
      ]}
      yScaleValueFormatter={yScaleValueFormatter}
      valueFormatter={valueFormatter}
      timeFilter={timeFilter}
      config={config}
      showKpisAsTooltip={true}
      tooltip={{
        labelFormatter: (value) => `${value}`,
        titleFormatter: (_value, tooltipItems) => {
          const dateValue =
            tooltipItems[0]?.raw &&
            typeof tooltipItems[0].raw === 'object' &&
            'x' in tooltipItems[0].raw
              ? (tooltipItems[0].raw as { x?: string | number })?.x
              : tooltipItems[0]?.parsed &&
                  typeof tooltipItems[0].parsed === 'object' &&
                  'x' in tooltipItems[0].parsed
                ? (tooltipItems[0].parsed as { x?: string | number })?.x
                : undefined;
          return `Date: ${formatDatetime(dateValue, { format: 'MMM dd, yyyy' })}`;
        },
        valueFormatter: (value) => `$${formatNumber(Math.abs(Number(value)))}`
      }}
    />
  );
}
